import React from 'react';
import styled from 'styled-components';
import { CsvImportGroups, CsvWorkspaceGroups, ImportAction, CsvVariable } from '../types';
import { AlertTitle, Poppins } from 'src/common';
import Radio from 'src/components/form/Radio';

const Container = styled.div`
  display: grid;
  grid-gap: 32px;
`;

const OptionsRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 12px;
  margin-left: 26px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

// Assessment scope group configs
interface AssessmentGroupConfig {
  key: keyof CsvImportGroups;
  title: string;
  getOptions: () => Array<{ value: ImportAction; label: string }>;
}

const assessmentGroupConfigs: AssessmentGroupConfig[] = [
  {
    key: 'customAssessmentVars',
    title: 'The following assessment variables already exist. Select how you would like to proceed with these:',
    getOptions: () => [
      { value: 'include', label: 'Replace with values imported' },
      { value: 'exclude', label: 'Exclude from import' },
    ],
  },
  {
    key: 'workspaceVars',
    title: 'The following variables exist as workspace variables. Select how you would like to proceed with these:',
    getOptions: () => [
      { value: 'include', label: 'Override' },
      { value: 'exclude', label: 'Exclude from import' },
    ],
  },
  {
    key: 'overriddenVars',
    title:
      'The following variables are overridden at the assessment level. Select how you would like to proceed with these:',
    getOptions: () => [
      { value: 'include', label: 'Replace with values imported' },
      { value: 'exclude', label: 'Exclude from import' },
    ],
  },
];

// Workspace scope group configs
interface WorkspaceGroupConfig {
  key: keyof CsvWorkspaceGroups;
  title: string;
  getOptions: () => Array<{ value: ImportAction; label: string }>;
}

const workspaceGroupConfigs: WorkspaceGroupConfig[] = [
  {
    key: 'existingVars',
    title: 'The following workspace variables already exist. Select how you would like to proceed with these:',
    getOptions: () => [
      { value: 'include', label: 'Replace with values imported' },
      { value: 'exclude', label: 'Exclude from import' },
    ],
  },
];

const formatVariableNames = (variables: CsvVariable[]): string => {
  return variables.map((v) => v.name).join(', ');
};

// Assessment scope selector
interface CSVGroupSelectorProps {
  groups: CsvImportGroups;
  onGroupsChange: (groups: CsvImportGroups) => void;
}

export const CSVGroupSelector: React.FC<CSVGroupSelectorProps> = ({ groups, onGroupsChange }) => {
  const handleActionChange = (groupKey: keyof CsvImportGroups, action: ImportAction) => {
    onGroupsChange({
      ...groups,
      [groupKey]: {
        ...groups[groupKey],
        action,
      },
    });
  };

  const activeGroups = assessmentGroupConfigs.filter((config) => groups[config.key].variables.length > 0);

  if (activeGroups.length === 0) {
    return null;
  }

  return (
    <Container>
      {activeGroups.map((config) => {
        const group = groups[config.key];
        const options = config.getOptions();

        return (
          <div key={config.key}>
            <AlertTitle>
              <Poppins px={14} color="statusRed">
                <Poppins px={14} color="statusRed" style={{ fontStyle: 'italic' }}>
                  {config.title}
                </Poppins>{' '}
                <Poppins px={14} color="statusRed" weight={600}>
                  {formatVariableNames(group.variables)}
                </Poppins>
              </Poppins>
            </AlertTitle>
            <OptionsRow>
              {options.map((option) => (
                <RadioOption key={option.value} onClick={() => handleActionChange(config.key, option.value)}>
                  <Radio medium isChecked={group.action !== option.value} />
                  <Poppins px={14} color="davysGrey" weight={500}>
                    {option.label}
                  </Poppins>
                </RadioOption>
              ))}
            </OptionsRow>
          </div>
        );
      })}
    </Container>
  );
};

// Workspace scope selector
interface CSVWorkspaceGroupSelectorProps {
  groups: CsvWorkspaceGroups;
  onGroupsChange: (groups: CsvWorkspaceGroups) => void;
}

export const CSVWorkspaceGroupSelector: React.FC<CSVWorkspaceGroupSelectorProps> = ({ groups, onGroupsChange }) => {
  const handleActionChange = (groupKey: keyof CsvWorkspaceGroups, action: ImportAction) => {
    onGroupsChange({
      ...groups,
      [groupKey]: {
        ...groups[groupKey],
        action,
      },
    });
  };

  const activeGroups = workspaceGroupConfigs.filter((config) => groups[config.key].variables.length > 0);

  if (activeGroups.length === 0) {
    return null;
  }

  return (
    <Container>
      {activeGroups.map((config) => {
        const group = groups[config.key];
        const options = config.getOptions();

        return (
          <div key={config.key}>
            <AlertTitle>
              <Poppins px={14} color="statusRed">
                <Poppins px={14} color="statusRed" style={{ fontStyle: 'italic' }}>
                  {config.title}
                </Poppins>{' '}
                <Poppins px={14} color="statusRed" weight={600}>
                  {formatVariableNames(group.variables)}
                </Poppins>
              </Poppins>
            </AlertTitle>
            <OptionsRow>
              {options.map((option) => (
                <RadioOption key={option.value} onClick={() => handleActionChange(config.key, option.value)}>
                  <Radio medium isChecked={group.action !== option.value} />
                  <Poppins px={14} color="davysGrey" weight={500}>
                    {option.label}
                  </Poppins>
                </RadioOption>
              ))}
            </OptionsRow>
          </div>
        );
      })}
    </Container>
  );
};

export default CSVGroupSelector;
